import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import type { Page, RecurringTask, Sort } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { can, usePermissions } from "@/auth/permissions";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { matchesFilter } from "@/lib/filter-predicate";
import { reportingNow } from "@/lib/reportClock";
import {
  projectLabel,
  userLabel,
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";
import { projectRecurring } from "@/modules/tasks/projections";
import { RecurringForm } from "@/modules/tasks/screens/RecurringForm";
import type { SectionScreenProps } from "@/modules/types";

function comparable(value: unknown): string | number {
  if (typeof value === "number") return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string") return value;
  return "";
}

/**
 * The schedules in the order asked for; with no sort, the ones coming due
 * soonest first and the paused ones last. A schedule with no next date (paused)
 * sorts after every one with a date, in either direction, as a blank date does
 * in the task lists.
 */
function sortRecurring(
  rows: readonly RecurringTask[],
  sort: readonly Sort[],
): RecurringTask[] {
  const effective: readonly Sort[] =
    sort.length > 0 ? sort : [{ column: "nextDate", direction: "asc" }];
  return [...rows].sort((a, b) => {
    for (const { column, direction } of effective) {
      const left = comparable(projectRecurring(a)[column]);
      const right = comparable(projectRecurring(b)[column]);
      if (left === right) continue;
      if (left === "") return 1;
      if (right === "") return -1;
      const order = left < right ? -1 : 1;
      return direction === "asc" ? order : -order;
    }
    return 0;
  });
}

/**
 * The Recurring section (#624): schedules that make an ordinary task each time
 * they come due. Everything is fetched once and narrowed, sorted and paged
 * here, as the task lists do: there are a handful of schedules, not thousands.
 */
export function Recurring({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();
  const permissions = usePermissions();
  const canCreate = can(permissions, "tasks.task.create");
  const canEdit = can(permissions, "tasks.task.edit");

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const start = cursor ? Number(cursor) : 0;
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RecurringTask | null>(null);

  const usersQuery = useUserDirectory();
  const projectsQuery = useProjectDirectory();
  const recurringQuery = useQuery({
    queryKey: ["tasks", "recurring"],
    queryFn: () => data.tasks.listRecurring(),
  });

  if (
    usersQuery.isPending ||
    projectsQuery.isPending ||
    recurringQuery.isPending
  ) {
    return <TableSkeleton />;
  }
  for (const query of [usersQuery, projectsQuery, recurringQuery]) {
    if (query.isError) {
      return (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      );
    }
  }

  const users = usersQuery.data?.items ?? [];
  const projects = projectsQuery.data?.items ?? [];
  const needle = (search ?? "").trim().toLowerCase();

  const matching = (recurringQuery.data ?? []).filter(
    (recurring) =>
      (needle === "" ||
        [
          recurring.title,
          projectRecurring(recurring).repeats as string,
          userLabel(users, recurring.assigneeUserId),
        ].some((text) => text.toLowerCase().includes(needle))) &&
      matchesFilter(projectRecurring(recurring), filter, reportingNow()),
  );
  const sorted = sortRecurring(matching, sort);
  const page: Page<RecurringTask> = {
    items: sorted.slice(start, start + limit),
    nextCursor: start + limit < sorted.length ? String(start + limit) : null,
    total: sorted.length,
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/*
        Hidden without `tasks.task.create`: a courtesy, not the enforcement.
        `POST /api/tasks/recurring` calls `requirePermission()` regardless, and
        a refusal still shows through the form's own text.
      */}
      {canCreate ? (
        <div className="flex shrink-0 justify-end border-b px-4 py-2">
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Plus />
            New recurring task
          </Button>
        </div>
      ) : null}

      <RecurringForm mode="create" open={creating} onOpenChange={setCreating} />
      {editing ? (
        <RecurringForm
          key={editing.id}
          mode="edit"
          recurring={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}

      <DataTable
        fields={fields}
        rows={page.items}
        getRowId={(recurring) => recurring.id}
        project={projectRecurring}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={
          canEdit
            ? (recurring) => {
                setEditing(recurring);
              }
            : undefined
        }
        renderOverride={(field, recurring) => {
          switch (field.name) {
            case "assigneeUserId":
              return <span>{userLabel(users, recurring.assigneeUserId)}</span>;
            case "projectId":
              return <span>{projectLabel(projects, recurring.projectId)}</span>;
            case "isActive":
              return recurring.isActive ? (
                <span>Active</span>
              ) : (
                <Badge variant="secondary">Paused</Badge>
              );
            case "nextDate":
              return recurring.nextDate === null ? (
                <span className="text-muted-foreground">Paused</span>
              ) : undefined;
            case "lastProblem":
              return recurring.lastProblem ? (
                <span className="text-destructive">
                  {recurring.lastProblem}
                </span>
              ) : (
                <span className="text-muted-foreground">None</span>
              );
            default:
              return undefined;
          }
        }}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={page}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
